import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { openCreateModal } from '@/features/sections/sectionsSlice';
import { openContentEnrichment } from '@/features/ui/uiSlice';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import luciLogo from '@/assets/images/luci_logo_min.png';
import styles from './Sidebar.module.css';

const WORKSPACE_NAV = [
  {
    id: 'home',
    label: 'Home',
    disabled: true,
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    id: 'library',
    label: 'Library',
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
  {
    id: 'projects',
    label: 'Projects',
    disabled: true,
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    id: 'studio',
    label: 'Studio',
    disabled: true,
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="23 7 16 12 23 17 23 7" />
        <rect x="1" y="5" width="15" height="14" rx="2" />
      </svg>
    ),
  },
];

const CREATE_NAV = [
  {
    id: 'creative-hub',
    label: 'Creative Hub',
    externalUrl: import.meta.env.VITE_CREATIVE_HUB_URL as string | undefined,
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    ),
  },
  {
    id: 'video-hub',
    label: 'Video Hub',
    externalUrl: import.meta.env.VITE_VIDEO_HUB_URL as string | undefined,
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    id: 'story-hub',
    label: 'StoryHub',
    externalUrl: import.meta.env.VITE_STORY_HUB_URL as string | undefined,
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="22" />
      </svg>
    ),
  },
];

const ORG_NAV = [
  {
    id: 'team',
    label: 'Team',
    disabled: true,
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    id: 'content-enrichment',
    label: 'Content Enrichment',
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
  },
  {
    id: 'brand-kit',
    label: 'Brand Kit',
    disabled: true,
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="13.5" cy="6.5" r="2.5" />
        <circle cx="17.5" cy="10.5" r="2.5" />
        <circle cx="8.5" cy="7.5" r="2.5" />
        <circle cx="6.5" cy="12.5" r="2.5" />
        <path d="M12 22a10 10 0 1 1 0-20" />
      </svg>
    ),
  },
  {
    id: 'settings',
    label: 'Settings',
    disabled: true,
    icon: (
      <svg className="navIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },
];

interface SidebarProps {
  activeItem?: string;
}

export default function Sidebar({ activeItem = 'library' }: SidebarProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const totalVideoCount = useAppSelector((s) => s.sections.totalVideoCount);
  const totalVideoDurationMs = useAppSelector((s) => s.sections.totalVideoDurationMs);
  const totalVideoSizeBytes = useAppSelector((s) => s.sections.totalVideoSizeBytes);

  // Format bytes → human-readable string e.g. "1.52 GB"
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 MB';
    const gb = bytes / (1024 ** 3);
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    const mb = bytes / (1024 ** 2);
    return `${mb.toFixed(0)} MB`;
  };

  // Format a duration in ms → compact "Xh Ym" / "Ym Zs" / "Zs"
  const formatDuration = (ms: number) => {
    const totalSec = Math.round(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);
  const [isCollapsed, setIsCollapsed] = useState(() => window.innerWidth <= 900);

  // Track mobile breakpoint; auto-collapse when crossing down into mobile
  useEffect(() => {
    const sync = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);
      if (mobile) setIsCollapsed(true);
    };
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  // On mobile the layout offset is always the collapsed width (64px).
  // On desktop it follows isCollapsed.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-w',
      isMobile || isCollapsed ? '64px' : '240px'
    );
  }, [isCollapsed, isMobile]);

  // Drawer open = mobile + not collapsed
  const isDrawerOpen = isMobile && !isCollapsed;

  const closeDrawer = () => setIsCollapsed(true);

  const handleNavClick = (id: string, externalUrl?: string) => {
    if (externalUrl) {
      trackEvent(ANALYTICS_EVENTS.NAV_EXTERNAL_HUB_CLICK, { hub: id });
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (id === 'content-enrichment') {
      trackEvent(ANALYTICS_EVENTS.CONTENT_ENRICHMENT_OPEN);
      dispatch(openContentEnrichment());
      return;
    }
    if (id === 'home' || id === 'library') navigate('/');
  };

  const renderNavItem = (item: { id: string; label: string; badge?: string; disabled?: boolean; externalUrl?: string; icon: JSX.Element }) => {
    const isActive = item.id === activeItem;
    const className = `${styles.navItem} ${isActive ? styles.navItemActive : ''} ${item.disabled ? styles.navItemDisabled : ''}`;

    if (item.externalUrl) {
      return (
        <a
          key={item.id}
          href={item.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          data-tooltip={item.label}
        >
          <span className={styles.navIcon}>{item.icon}</span>
          <span className={styles.navLabel}>{item.label}</span>
        </a>
      );
    }

    return (
      <button
        key={item.id}
        className={className}
        onClick={() => !item.disabled && handleNavClick(item.id, item.externalUrl)}
        aria-current={isActive ? 'page' : undefined}
        aria-disabled={item.disabled}
        data-tooltip={item.label}
        tabIndex={item.disabled ? -1 : 0}
      >
        <span className={styles.navIcon}>{item.icon}</span>
        <span className={styles.navLabel}>{item.label}</span>
        {item.badge && <span className={styles.navBadge}>{item.badge}</span>}
        {item.id === 'library' && totalVideoCount > 0 && (
          <span className={styles.navBadge}>{totalVideoCount}</span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Backdrop — only visible when drawer is open on mobile */}
      {isDrawerOpen && (
        <div
          className={styles.backdrop}
          onClick={closeDrawer}
          aria-hidden="true"
        />
      )}
      <aside
        className={styles.sidebar}
        data-collapsed={String(isCollapsed)}
        data-drawer={String(isDrawerOpen)}
        aria-label="Main navigation"
      >
      {/* Logo row + collapse toggle */}
      <div className={styles.logoRow}>
        <a href="/" className={styles.logo}>
          <img src={luciLogo} alt="Lucihub" className={styles.logoMark} />
          <span className={styles.logoText}>Lucihub</span>
        </a>
        <button
          className={styles.collapseToggle}
          onClick={() => setIsCollapsed((c) => !c)}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {/* Left-pointing chevron — rotated 180° by CSS when collapsed */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      </div>

      {/* WORKSPACE */}
      <div className={styles.navSection}>
        <div className={styles.sectionLabel}>Workspace</div>
        {WORKSPACE_NAV.map(renderNavItem)}
      </div>

      {/* CREATE */}
      <div className={styles.navSection}>
        <div className={styles.sectionLabel}>Create</div>
        {CREATE_NAV.map(renderNavItem)}
      </div>

      {/* ORGANIZATION */}
      <div className={styles.navSection}>
        <div className={styles.sectionLabel}>Organization</div>
        {ORG_NAV.map(renderNavItem)}
      </div>

      {/* New Collection button */}
      <div className={styles.newCollectionRow}>
        <button
          className={styles.newCollectionBtn}
          onClick={() => dispatch(openCreateModal())}
          data-tooltip="New Collection"
          aria-label="New Collection"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span className={styles.navLabel}>New Collection</span>
        </button>
      </div>

      {/* Library stats — total videos and combined duration */}
      <div className={styles.libraryStats}>
        <div className={styles.statRow}>
          <span className={styles.statIcon} aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <polygon points="10 8 15 10.5 10 13 10 8" fill="currentColor" stroke="none" />
              <line x1="8" y1="21" x2="16" y2="21" />
            </svg>
          </span>
          <span className={styles.statLabel}>Videos</span>
          <span className={styles.statValue}>{totalVideoCount.toLocaleString()}</span>
        </div>
        <div className={styles.statRow}>
          <span className={styles.statIcon} aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 7 12 12 15.5 14" />
            </svg>
          </span>
          <span className={styles.statLabel}>CI duration</span>
          <span className={styles.statValue}>{formatDuration(totalVideoDurationMs)}</span>
        </div>
      </div>

      {/* Storage */}
      <div className={styles.storageSection}>
        <div className={styles.storageIcon} aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="5" rx="9" ry="3" />
            <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
            <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
          </svg>
        </div>
        <div className={styles.storageLabel}>Storage</div>
        <div className={styles.storageBar}>
          <div className={styles.storageBarFill} style={{ width: `${Math.min(100, (totalVideoSizeBytes / (200 * 1024 ** 3)) * 100).toFixed(1)}%` }} />
        </div>
        <div className={styles.storageMeta}>{formatBytes(totalVideoSizeBytes)} / 200 GB </div>
      </div>
      </aside>
    </>
  );
}
